/**
 * Screen Reader Announcement Service
 * Provides utilities for making announcements to screen readers
 */

interface AnnouncementOptions {
  priority?: 'polite' | 'assertive';
  delay?: number;
  clear?: boolean;
}

class ScreenReaderAnnouncements {
  private politeRegion: HTMLElement | null = null;
  private assertiveRegion: HTMLElement | null = null;
  private initialized = false;

  /**
   * Initialize the announcement regions
   * Should be called once when the application starts
   */
  init(): void {
    if (this.initialized || typeof document === 'undefined') return;

    // Create polite announcement region
    this.politeRegion = document.createElement('div');
    this.politeRegion.setAttribute('aria-live', 'polite');
    this.politeRegion.setAttribute('aria-atomic', 'true');
    this.politeRegion.setAttribute('aria-relevant', 'text');
    this.politeRegion.className = 'sr-only';
    this.politeRegion.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;

    // Create assertive announcement region
    this.assertiveRegion = document.createElement('div');
    this.assertiveRegion.setAttribute('aria-live', 'assertive');
    this.assertiveRegion.setAttribute('aria-atomic', 'true');
    this.assertiveRegion.setAttribute('aria-relevant', 'text');
    this.assertiveRegion.className = 'sr-only';
    this.assertiveRegion.style.cssText = this.politeRegion.style.cssText;

    // Add to document
    document.body.appendChild(this.politeRegion);
    document.body.appendChild(this.assertiveRegion);

    this.initialized = true;
  }

  /**
   * Make an announcement to screen readers
   */
  announce(message: string, options: AnnouncementOptions = {}): void {
    if (!this.initialized) {
      this.init();
    }

    const { priority = 'polite', delay = 0, clear = false } = options;
    const region = priority === 'assertive' ? this.assertiveRegion : this.politeRegion;

    if (!region) return;

    const makeAnnouncement = () => {
      if (clear) {
        // Clear the region first, then announce
        region.textContent = '';
        setTimeout(() => {
          region.textContent = message;
        }, 10);
      } else {
        region.textContent = message;
      }
    };

    if (delay > 0) {
      setTimeout(makeAnnouncement, delay);
    } else {
      makeAnnouncement();
    }
  }

  /**
   * Announce an error message
   */
  announceError(message: string, fieldId?: string): void {
    let announcement = `Error: ${message}`;
    
    if (fieldId) {
      const field = document.getElementById(fieldId);
      if (field) {
        const label = field.getAttribute('aria-label') || 
                     field.getAttribute('placeholder') ||
                     document.querySelector(`label[for="${fieldId}"]`)?.textContent;
        if (label) {
          announcement = `Error in ${label}: ${message}`;
        }
      }
    }

    this.announce(announcement, { priority: 'assertive' });
  }

  /**
   * Announce a success message
   */
  announceSuccess(message: string): void {
    this.announce(`Success: ${message}`, { priority: 'polite' });
  }

  /**
   * Announce form validation results
   */
  announceValidation(errors: string[], fieldId?: string): void {
    if (errors.length === 0) return;

    const message = errors.length === 1 
      ? errors[0]
      : `${errors.length} errors: ${errors.join(', ')}`;

    this.announceError(message, fieldId);
  }

  /**
   * Announce navigation changes
   */
  announceNavigation(pageName: string, additionalInfo?: string): void {
    let message = `Navigated to ${pageName}`;
    if (additionalInfo) {
      message += `, ${additionalInfo}`;
    }
    this.announce(message, { priority: 'polite', delay: 100 });
  }

  /**
   * Announce loading states
   */
  announceLoading(isLoading: boolean, context?: string): void {
    const contextText = context ? ` ${context}` : '';
    const message = isLoading 
      ? `Loading${contextText}...`
      : `Finished loading${contextText}`;
    
    this.announce(message, { priority: 'polite' });
  }

  /**
   * Announce dynamic content changes
   */
  announceContentChange(description: string): void {
    this.announce(description, { priority: 'polite', delay: 500 });
  }

  /**
   * Clear all announcements
   */
  clear(): void {
    if (this.politeRegion) {
      this.politeRegion.textContent = '';
    }
    if (this.assertiveRegion) {
      this.assertiveRegion.textContent = '';
    }
  }

  /**
   * Cleanup - remove announcement regions
   */
  destroy(): void {
    if (this.politeRegion) {
      document.body.removeChild(this.politeRegion);
      this.politeRegion = null;
    }
    if (this.assertiveRegion) {
      document.body.removeChild(this.assertiveRegion);
      this.assertiveRegion = null;
    }
    this.initialized = false;
  }
}

// Create singleton instance
export const screenReaderAnnouncements = new ScreenReaderAnnouncements();

// Initialize on module load if in browser environment
if (typeof window !== 'undefined') {
  // Initialize after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      screenReaderAnnouncements.init();
    });
  } else {
    screenReaderAnnouncements.init();
  }
}

/**
 * React hook for screen reader announcements
 */
import { useCallback } from 'react';

export function useScreenReaderAnnouncements() {
  const announce = useCallback((message: string, options?: AnnouncementOptions) => {
    screenReaderAnnouncements.announce(message, options);
  }, []);

  const announceError = useCallback((message: string, fieldId?: string) => {
    screenReaderAnnouncements.announceError(message, fieldId);
  }, []);

  const announceSuccess = useCallback((message: string) => {
    screenReaderAnnouncements.announceSuccess(message);
  }, []);

  const announceValidation = useCallback((errors: string[], fieldId?: string) => {
    screenReaderAnnouncements.announceValidation(errors, fieldId);
  }, []);

  const announceNavigation = useCallback((pageName: string, additionalInfo?: string) => {
    screenReaderAnnouncements.announceNavigation(pageName, additionalInfo);
  }, []);

  const announceLoading = useCallback((isLoading: boolean, context?: string) => {
    screenReaderAnnouncements.announceLoading(isLoading, context);
  }, []);

  const announceContentChange = useCallback((description: string) => {
    screenReaderAnnouncements.announceContentChange(description);
  }, []);

  return {
    announce,
    announceError,
    announceSuccess,
    announceValidation,
    announceNavigation,
    announceLoading,
    announceContentChange
  };
}